import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { PublicLayout } from '@/components/PublicLayout';
import { api } from '@/utils/api';
import { Calendar, Tag, ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const NewsDetail = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const [newsItem, setNewsItem] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNews();
  }, [id]);

  const loadNews = async () => {
    try {
      const response = await api.get('/news/public');
      const found = response.data.find(n => n.news_id === id);
      setNewsItem(found);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCategoryLabel = (category) => {
    return t.news.categories[category] || category;
  };

  const getCategoryColor = (category) => {
    const colors = {
      event: 'bg-purple-100 text-purple-700',
      news: 'bg-blue-100 text-blue-700',
      announcement: 'bg-green-100 text-green-700',
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  // Helper to get correct image URL
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('/')) {
      return `${BACKEND_URL}${imageUrl}`;
    }
    return imageUrl;
  };

  if (loading) {
    return (
      <PublicLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-12 h-12 border-4 border-[#127abb] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </PublicLayout>
    );
  }

  if (!newsItem) {
    return (
      <PublicLayout>
        <div className="container mx-auto px-4 py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">
            {language === 'fr' ? 'Actualité non trouvée' : 'News not found'}
          </h1>
          <Link to="/news">
            <Button className="bg-[#127abb] hover:bg-[#0e6292]">
              {language === 'fr' ? 'Retour aux actualités' : 'Back to news'}
            </Button>
          </Link>
        </div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-8 lg:px-12">
          <Link to="/news" className="inline-flex items-center gap-2 text-[#127abb] hover:text-[#0e6292] mb-8 font-medium">
            <ArrowLeft size={20} />
            {t.news.title}
          </Link>

          <article className="max-w-4xl mx-auto">
            <div className="h-96 rounded-2xl overflow-hidden shadow-xl mb-8 bg-slate-100">
              {newsItem.image_url ? (
                <img
                  src={getImageUrl(newsItem.image_url)}
                  alt={language === 'fr' ? newsItem.title_fr : newsItem.title_en}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-full h-full flex items-center justify-center ${newsItem.image_url ? 'hidden' : ''}`}>
                <ImageIcon className="text-slate-300" size={64} />
              </div>
            </div>

            <div className="flex items-center gap-4 mb-6">
              <span className={`text-sm font-medium px-4 py-2 rounded-full ${getCategoryColor(newsItem.category)}`}>
                <Tag size={14} className="inline mr-2" />
                {getCategoryLabel(newsItem.category)}
              </span>
              <span className="text-sm text-slate-500 flex items-center gap-2">
                <Calendar size={14} />
                {format(new Date(newsItem.created_at), 'dd/MM/yyyy')}
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              {language === 'fr' ? newsItem.title_fr : newsItem.title_en}
            </h1>

            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-slate-700 leading-relaxed whitespace-pre-wrap">
                {language === 'fr' ? newsItem.content_fr : newsItem.content_en}
              </p>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <Link to="/contact">
                <Button className="bg-[#127abb] hover:bg-[#0e6292] text-white rounded-full px-8 py-6">
                  {language === 'fr' ? 'Contactez-nous pour plus d\'informations' : 'Contact us for more information'}
                </Button>
              </Link>
            </div>
          </article>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NewsDetail;